import { Injectable, NgZone } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  delay,
  map,
  Observable,
  of,
  throwError,
} from 'rxjs';
import { Router } from '@angular/router';
import { SharedService } from '../../core_modules/common/services/shared.service';
import { ApiConfig } from '../../core_modules/utils/api-utils/api-config';
import {
  LocalStorage,
  LocalStorageUtil,
} from '../../core_modules/utils/local-storage-util';
import {OAuthTokenResponse, RefreshToken} from '../../core_modules/common/models/refresh-token.model';
import { ModalService } from '../../common_resource_modules/services/modal.service';
import { environment } from '../../../environments/environment';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { MenuStorageService } from '../../common_resource_modules/services/menu-storage.service';
import { AdminUserService } from '../../admin_modules/services/admin-user.service';
import {tap} from "rxjs/operators";

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isWideLayout: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private lastActivity = Date.now();
  constructor(
    private ModalService: ModalService,
    private httpClient: HttpClient,
    private sharedService: SharedService,
    private router: Router,
    private menuStorageService: MenuStorageService,
    private userService: AdminUserService,
    private ngZone: NgZone,
  ) {
    this.ngZone.runOutsideAngular(() => {
      ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'].forEach((evt) =>
        window.addEventListener(
          evt,
          () => (this.lastActivity = Date.now()),
          { passive: true },
        ),
      );
    });
  }


  authenticate(
    username: string,
    password: string,
    showMessage?: boolean,
  ): Observable<any> {
    const body = new HttpParams()
      .set('username', username)
      .set('password', password)
      .set('scope', 'openid')
      .set('grant_type', 'password');

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: 'Basic bXktY2xpZW50Om15LWNsaWVudA==',
    });
    return this.httpClient
      .post<any>(`${ApiConfig.login}`, body.toString(), { headers })
      .pipe(
        delay(1500),
        map((res: Response) => {
          if (showMessage) {
            // this.sharedService.getHttpSuccessResponseMessage(res);
          }
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

  loginToMbank(
    username: string,
    password: string,
    tfaMethod?: string,
    otpCode?: string,
    tfaAction?: string,
    showMessage?: boolean,
  ): Observable<any> {
    let deviceUniqueIdentifier: string;
    let smsReadToken: string;

    let params = new HttpParams()
      .set('grant_type', 'password')
      .set('scope', 'openid')
      .set('username', username)
      .set('password', password);

    if (tfaMethod) {
      params = params.set('tfaMethod', tfaMethod);
    }
    if (otpCode) {
      params = params.set('otpCode', otpCode);
    }
    if (tfaAction) {
      params = params.set('tfaAction', tfaAction);
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: 'Basic bXktY2xpZW50Om15LWNsaWVudA==',
    });

    return this.httpClient
      .post<any>(`${ApiConfig.login}`, params.toString(), { headers })
      .pipe(
        delay(1500),
        map((res: any) => {
          if (showMessage) {
            // this.sharedService.getHttpSuccessResponseMessage(res);
          }
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

  getTfaMethods(): Observable<any> {
    return this.httpClient.get<any>(`${ApiConfig.TFA_METHODS}`);
  }

  generateTfaCode(request: any): Observable<any> {
    return this.httpClient.post<any>(`${ApiConfig.TFA_GENERATE_CODE}`, request);
  }

  forgetPassword(
    username: string | undefined,
    showMessage?: boolean,
  ): Observable<any> {
    return this.httpClient
      .get<any>(
        `${ApiConfig.baseApiEndPoint}/user/forgot-password/${username}`,
        {},
      )
      .pipe(
        delay(1500),
        map((res: Response) => {
          if (showMessage) {
            this.sharedService.getHttpSuccessResponseMessage(res);
          }
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          this.sharedService.getServerErrorMessage(error.error);
          return throwError(() => error);
        }),
      );
  }

  changePassword(requestData: any, showMessage?: boolean): Observable<any> {
    return this.httpClient
      .post<any>(
        `${ApiConfig.baseApiEndPoint}/user/change/Password`,
        requestData,
        {},
      )
      .pipe(
        delay(1500),
        map((res: Response) => {
          if (showMessage) {
            this.sharedService.getHttpSuccessResponseMessage(res);
          }
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          this.sharedService.getServerErrorMessage(error.error);
          return throwError(() => error);
        }),
      );
  }

  public onResetPassword(data: any, showMessage?: boolean): Observable<any> {
    const api = `${ApiConfig.auth}/reset-password`;
    return this.httpClient.post<any>(api, data).pipe(
      map((res: any) => {
        if (showMessage) {
          this.sharedService.getHttpSuccessResponseMessage(res);
        }
        return res;
      }),
      catchError((error) => {
        this.sharedService.getServerErrorMessage(error.error);
        return throwError(() => error);
      }),
    );
  }

  verifyOTP(loginForm: any, showMessage?: boolean) {
    return this.httpClient.post<any>(`${ApiConfig.VERIFY_OTP}`, loginForm).pipe(
      delay(1500),
      map((res: Response) => {
        if (showMessage) {
          // this.sharedService.getHttpSuccessResponseMessage(res);
        }
        return res;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        // this.sharedService.getServerErrorMessage(error.error);
        return throwError(() => error);
      }),
    );
  }

  logout() {
    this.ModalService.closeModal();
    this.userService.logOutUser().subscribe({
      next: (response: any) => {
        LocalStorageUtil.setStorage(new LocalStorage());
        this.menuStorageService.clearMenu();
        this.router.navigate(['/auth/login']);
      },
      error: (error) => {
        console.error(error);
        return;
      },
    });
  }

  clearLocalStorage() {
    this.ModalService.closeModal();
    this.menuStorageService.clearMenu();
    document.documentElement.classList.remove('dark-mode');
    localStorage.removeItem('onboardingCompleted');
    LocalStorageUtil.setStorage(new LocalStorage());
    localStorage.clear();
  }

  public refreshAccessToken(showMessage?: boolean): Observable<any> {
    let tokenDetails: RefreshToken = new RefreshToken();
    tokenDetails.accessToken = LocalStorageUtil.getStorage().at;
    tokenDetails.refreshToken = LocalStorageUtil.getStorage().rt;
    tokenDetails.tokenType = 'Bearer';
    return this.httpClient
      .post<any>(`${ApiConfig.refreshToken}`, tokenDetails)
      .pipe(
        map((res: Response) => {
          if (showMessage) {
            this.sharedService.getHttpSuccessResponseMessage(res);
          }
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          // this.sharedService.getServerErrorMessage(error.error);
          return throwError(() => error);
        }),
      );
  }

  public resetUserPassword(
    username: any,
    showMessage?: boolean,
  ): Observable<any> {
    const api = `${ApiConfig.auth}/reset-user-password?username=${username}`;
    return this.httpClient.post<any>(api, username).pipe(
      map((res: any) => {
        if (showMessage) {
          this.sharedService.getHttpSuccessResponseMessage(res);
        }
        return res;
      }),
      catchError((error) => {
        this.sharedService.getServerErrorMessage(error.error);
        return throwError(() => error);
      }),
    );
  }

  initialData(showMessage?: boolean) {
    return this.httpClient.get<any>(`${ApiConfig.baseApiUrl}/initialdata`).pipe(
      map((res: any) => {
        if (showMessage) {
          this.sharedService.getHttpSuccessResponseMessage(res);
        }
        return res;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      }),
    );
  }

  public refreshWithOAuth(): Observable<OAuthTokenResponse> {
    const session = LocalStorageUtil.getStorage();

    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('refresh_token', session?.rt ?? '')

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: 'Basic bXktY2xpZW50Om15LWNsaWVudA==',
    });

    return this.httpClient
      .post<OAuthTokenResponse>(`${ApiConfig.login}`, body.toString(), { headers })
      .pipe(
        tap((res) => {

          const current = LocalStorageUtil.getStorage();
          current.at = res.access_token;
          current.rt = res.refresh_token ?? current.rt;
          current.et = res.expires_in;
          LocalStorageUtil.setStorage(current);
        }),
      );
  }
  isUserActive(maxIdleMs = 30 * 1000): boolean {
    return Date.now() - this.lastActivity < maxIdleMs;
  }
}
